import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Switch,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { formatDuration } from '../../utils/timeUtils';
import { WORKING_DAY_IN_HOURS } from '../../config/timeConstants';
import ColorPicker from '../../components/ColorPicker';

// Ajouter ces constantes au début du fichier, après les imports
const PARTICULARITY_TYPES = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
  VARIABLE: 'variable',
  CALENDAR: 'calendar'
};

const PARTICULARITY_LABELS = {
  [PARTICULARITY_TYPES.MONTHLY]: 'Mensuel',
  [PARTICULARITY_TYPES.ANNUAL]: 'Annuel',
  [PARTICULARITY_TYPES.VARIABLE]: 'Variable',
  [PARTICULARITY_TYPES.CALENDAR]: 'Calendaire'
};

function ReleveCodeManagement() {
  const [releveCodes, setReleveCodes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showInactiveCodes, setShowInactiveCodes] = useState(false);
  const [currentCode, setCurrentCode] = useState({
    code: '',
    hours_per_month: '',
    monthly_limit: '', // Ajout du nouveau champ
    destination: '',
    particularity: 'monthly',
    role: '',
    mandate_description: '',
    is_active: true,
    color: '',
    include_in_releve_rate: false,
    without_mandate: false  // Ajout du nouveau champ
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [orderBy, setOrderBy] = useState('code');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [localDays, setLocalDays] = useState('');
  const [localHours, setLocalHours] = useState('');
  const [localLimitDays, setLocalLimitDays] = useState('');
  const [localLimitHours, setLocalLimitHours] = useState('');

  useEffect(() => {
    const fetchReleveCodes = async () => {
      try {
        const { data, error } = await supabase
          .from('releve_codes')
          .select('*')
          .order(orderBy, { ascending: orderDirection === 'asc' });

        if (error) throw error;
        setReleveCodes(data);
      } catch (error) {
        console.error('Erreur lors du chargement des codes de relève:', error);
        showSnackbar('Erreur lors du chargement des codes de relève', 'error');
      }
    };
    fetchReleveCodes();
  }, [orderBy, orderDirection]);

  useEffect(() => {
    if (openDialog) {
      setLocalHours(currentCode.hours_per_month || '');
      setLocalDays(hoursToWorkDays(currentCode.hours_per_month));
      setLocalLimitHours(currentCode.monthly_limit || '');
      setLocalLimitDays(hoursToWorkDays(currentCode.monthly_limit));
    }
  }, [openDialog, currentCode.hours_per_month, currentCode.monthly_limit]);

  const handleOpenDialog = (code = null) => {
    if (code) {
      setCurrentCode({
        id: code.id,
        code: code.code,
        hours_per_month: code.hours_per_month,
        monthly_limit: code.monthly_limit,
        destination: code.destination,
        particularity: code.particularity || 'monthly',
        role: code.role || '',
        mandate_description: code.mandate_description || '',
        is_active: code.is_active,
        color: code.color || '',
        include_in_releve_rate: code.include_in_releve_rate || false,
        without_mandate: code.without_mandate || false
      });
      setIsEditing(true);
    } else {
      setCurrentCode({
        code: '',
        hours_per_month: '',
        monthly_limit: '',
        destination: '',
        particularity: 'monthly',
        role: '',
        mandate_description: '',
        is_active: true,
        color: '',
        include_in_releve_rate: false,
        without_mandate: false
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCode({
      code: '',
      hours_per_month: '',
      monthly_limit: '',
      destination: '',
      particularity: 'monthly',
      role: '',
      mandate_description: '',
      is_active: true,
      color: '',
      include_in_releve_rate: false,
      without_mandate: false
    });
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setCurrentCode(prev => ({
      ...prev,
      [name]: name === 'is_active' || name === 'include_in_releve_rate' || name === 'without_mandate' ? checked : value
    }));
  };

  // Nouvelles fonctions de conversion et de gestion
  const hoursToWorkDays = (hours) => {
    if (!hours) return '';
    return (hours / WORKING_DAY_IN_HOURS).toFixed(2);
  };

  const workDaysToHours = (days) => {
    if (!days) return '';
    return (days * WORKING_DAY_IN_HOURS).toFixed(2);
  };

  const handleDaysChange = (e) => {
    setLocalDays(e.target.value);
  };

  const handleHoursChange = (e) => {
    setLocalHours(e.target.value);
  };

  const handleDaysBlur = (e) => {
    const days = parseFloat(e.target.value);
    if (!isNaN(days)) {
      const hours = workDaysToHours(days);
      setCurrentCode(prev => ({
        ...prev,
        hours_per_month: hours
      }));
      setLocalHours(hours);
    }
  };

  const handleHoursBlur = (e) => {
    const hours = parseFloat(e.target.value);
    if (!isNaN(hours)) {
      setCurrentCode(prev => ({
        ...prev,
        hours_per_month: hours.toString()
      }));
      setLocalDays(hoursToWorkDays(hours));
    }
  };

  const handleLimitDaysChange = (e) => {
    setLocalLimitDays(e.target.value);
  };

  const handleLimitHoursChange = (e) => {
    setLocalLimitHours(e.target.value);
  };

  const handleLimitDaysBlur = (e) => {
    const days = parseFloat(e.target.value);
    if (!isNaN(days)) {
      const hours = workDaysToHours(days);
      setCurrentCode(prev => ({
        ...prev,
        monthly_limit: hours
      }));
      setLocalLimitHours(hours);
    }
  };

  const handleLimitHoursBlur = (e) => {
    const hours = parseFloat(e.target.value);
    if (!isNaN(hours)) {
      setCurrentCode(prev => ({
        ...prev,
        monthly_limit: hours.toString()
      }));
      setLocalLimitDays(hoursToWorkDays(hours));
    }
  };

  const validateCode = (code) => {
    const specialCharPattern = /[^a-zA-Z0-9]/;
    if (specialCharPattern.test(code)) {
      showSnackbar("Le champ 'code' ne doit pas contenir de caractères spéciaux.", 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!currentCode.code || !currentCode.destination) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }

      if (!validateCode(currentCode.code)) {
        return;
      }

      // Convertir et valider la dotation
      let hoursPerMonth = null;
      if (currentCode.hours_per_month) {
        const hours = parseFloat(currentCode.hours_per_month);
        if (isNaN(hours) || hours < 0) {
          throw new Error('La dotation doit être un nombre positif');
        }
        hoursPerMonth = Number(hours.toFixed(2));
      }

      const releveCodeData = {
        code: currentCode.code,
        hours_per_month: hoursPerMonth,
        monthly_limit: currentCode.monthly_limit ? Number(currentCode.monthly_limit) : null,
        destination: currentCode.destination,
        particularity: currentCode.particularity,
        role: currentCode.role || null,
        mandate_description: currentCode.mandate_description || null,
        is_active: currentCode.is_active,
        color: currentCode.color,
        include_in_releve_rate: currentCode.include_in_releve_rate,
        without_mandate: currentCode.without_mandate
      };

      if (isEditing) {
        await supabase
          .from('releve_codes')
          .update(releveCodeData)
          .eq('id', currentCode.id);
      } else {
        await supabase
          .from('releve_codes')
          .insert(releveCodeData);
      }

      handleCloseDialog();
      fetchReleveCodes();
      showSnackbar(
        isEditing ? 'Code de relève mis à jour avec succès' : 'Code de relève créé avec succès',
        'success'
      );
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showSnackbar(error.message || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      const { error } = await supabase
        .from('releve_codes')
        .update({ is_active: !code.is_active })
        .eq('id', code.id);

      if (error) throw error;

      fetchReleveCodes();
      showSnackbar(
        code.is_active
          ? 'Code de relève désactivé avec succès'
          : 'Code de relève réactivé avec succès',
        'success'
      );
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      showSnackbar(error.message || 'Erreur lors de la modification du statut', 'error');
    }
  };

  const handleDeleteReleveCode = async (codeId) => {
    try {
      // Vérifier d'abord s'il y a des mandats liés
      const { count: countMandats, error: checkErrorMandats } = await supabase
        .from('user_mandates')
        .select('*', { count: 'exact' })
        .eq('releve_code_id', codeId);

      if (checkErrorMandats) throw checkErrorMandats;

      // Vérifier d'abord s'il y a des pointages liés
      const { count: countPointages, error: checkErrorpointages } = await supabase
        .from('pointage')
        .select('*', { count: 'exact' })
        .eq('releve_code_id', codeId);

      if (checkErrorpointages) throw checkErrorpointages;

      if (countMandats + countPointages > 0) {
        // S'il y a des mandats ou des pointages liés, désactiver le code au lieu de le supprimer
        const { error: updateError } = await supabase
          .from('releve_codes')
          .update({ is_active: false })
          .eq('id', codeId);

        if (updateError) throw updateError;

        showSnackbar('Le code de relève a été désactivé car il est encore utilisé', 'warning');
      } else {
        // Supprimer si aucun mandat lié
        const { error: deleteError } = await supabase
          .from('releve_codes')
          .delete()
          .eq('id', codeId);

        if (deleteError) throw deleteError;

        showSnackbar('Code de relève supprimé avec succès', 'success');
      }

      fetchReleveCodes();
    } catch (error) {
      console.error('Erreur lors de la suppression du code de relève:', error);
      showSnackbar(error.message || 'Erreur lors de la suppression du code de relève', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSort = (column) => {
    // Si la colonne est déjà triée, inverser la direction
    // Sinon, commencer par un tri ascendant
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  // Remplacement des labels
  const roleLabel = 'Rôle';
  const dotationLabel = 'Dotation';
  const validiteLabel = 'Validité';

  function generateColorShades(color) {
    if (!color || color === '') return []; // Vérification de la couleur
    const shades = [];
    for (let i = 1; i <= 4; i++) {
      const shade = adjustColor(color, i * 10); // Adjust color by 10% for each shade
      shades.push(shade);
    }
    return shades;
  }

  function adjustColor(color, percent) {
    const num = parseInt(color.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const R = ((num >> 16) + amt);
    const G = (((num >> 8) & 0x00FF) + amt);
    const B = ((num & 0x0000FF) + amt);
    return "#" + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
  }

  const fetchReleveCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('releve_codes')
        .select('*')
        .order(orderBy, { ascending: orderDirection === 'asc' });

      if (error) throw error;
      setReleveCodes(data);
    } catch (error) {
      console.error('Erreur lors du chargement des codes de relève:', error);
      showSnackbar('Erreur lors du chargement des codes de relève', 'error');
    }
  };

  const getParticularityLabel = (particularity) => {
    return PARTICULARITY_LABELS[particularity] || '-';
  };

  return (
    <><Box sx={{ p: 3 }}>
      <Box sx={{
        marginBottom: 2
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1
        }}>
          <Typography variant="h4">Codes</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Ajouter
          </Button>
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography>Codes Inactifs</Typography>
          <Switch
            checked={showInactiveCodes}
            onChange={() => setShowInactiveCodes(!showInactiveCodes)}
            color="primary"
          />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '8%' }}>
                <TableSortLabel
                  active={orderBy === 'code'}
                  direction={orderBy === 'code' ? orderDirection : 'asc'}
                  onClick={() => handleSort('code')}
                >
                  Code
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '10%' }}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? orderDirection : 'asc'}
                  onClick={() => handleSort('role')}
                >
                  {roleLabel}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '12%' }}>
                <TableSortLabel
                  active={orderBy === 'destination'}
                  direction={orderBy === 'destination' ? orderDirection : 'asc'}
                  onClick={() => handleSort('destination')}
                >
                  Destination
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '15%' }}>
                <TableSortLabel
                  active={orderBy === 'mandate_description'}
                  direction={orderBy === 'mandate_description' ? orderDirection : 'asc'}
                  onClick={() => handleSort('mandate_description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '8%' }}>
                <TableSortLabel
                  active={orderBy === 'hours_per_month'}
                  direction={orderBy === 'hours_per_month' ? orderDirection : 'asc'}
                  onClick={() => handleSort('hours_per_month')}
                >
                  {dotationLabel}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '8%' }}>
                <TableSortLabel
                  active={orderBy === 'particularity'}
                  direction={orderBy === 'particularity' ? orderDirection : 'asc'}
                  onClick={() => handleSort('particularity')}
                >
                  {validiteLabel}
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '8%' }}>
                <TableSortLabel
                  active={orderBy === 'monthly_limit'}
                  direction={orderBy === 'monthly_limit' ? orderDirection : 'asc'}
                  onClick={() => handleSort('monthly_limit')}
                >
                  Limite
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: '7%' }}>Couleur</TableCell>
              <TableCell style={{ width: '5%', padding: '6px' }}>Actif</TableCell>
              <TableCell style={{ width: '5%', padding: '6px' }}>Taux</TableCell>
              <TableCell style={{ width: '5%', padding: '6px' }}>Mandat</TableCell>
              <TableCell style={{ width: '8%', padding: '6px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {releveCodes
              .filter(code => showInactiveCodes || code.is_active)
              .map((code) => (
                <TableRow
                  key={code.id}
                  sx={{
                    backgroundColor: !code.is_active ? '#f5f5f5' : 'inherit',
                    opacity: !code.is_active ? 0.7 : 1,
                    '& .MuiSwitch-root': {
                      margin: '-8px',
                      transform: 'scale(0.7)',
                    }
                  }}
                >
                  <TableCell sx={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code.code}</TableCell>
                  <TableCell sx={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code.role || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code.destination || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code.mandate_description || '-'}</TableCell>
                  <TableCell>{code.hours_per_month ? formatDuration(code.hours_per_month) : '-'}</TableCell>
                  <TableCell>
                    {getParticularityLabel(code.particularity)}
                  </TableCell>
                  <TableCell>
                    {code.monthly_limit ? formatDuration(code.monthly_limit) : '-'}
                  </TableCell>
                  <TableCell style={{ backgroundColor: generateColorShades(code.color)[0], color: '#fff' }}>
                    {code.color ? '' : '-'}
                  </TableCell>
                  <TableCell sx={{ padding: '6px' }}>
                    <Switch
                      size="small"
                      checked={code.is_active}
                      onChange={() => handleToggleActive(code)}
                      color="primary" />
                  </TableCell>
                  <TableCell sx={{ padding: '6px' }}>
                    <Switch
                      size="small"
                      checked={code.include_in_releve_rate || false}
                      disabled
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '6px' }}>
                    <Switch
                      size="small"
                      checked={code.without_mandate || false}
                      disabled
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '6px' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(code)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteReleveCode(code.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Modifier le code de relève' : 'Nouveau code de relève'}
        </DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={2}>
            <TextField
              name="code"
              label="Code"
              value={currentCode.code}
              onChange={handleChange}
              required 
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Dotation (jours)"
                type="number"
                value={localDays}
                onChange={handleDaysChange}
                onBlur={handleDaysBlur}
                inputProps={{ 
                  step: 1,
                  min: 0
                }}
                fullWidth
              />
              <TextField
                label="Dotation (heures)"
                type="number"
                value={localHours}
                onChange={handleHoursChange}
                onBlur={handleHoursBlur}
                inputProps={{ 
                  step: 0.25,
                  min: 0
                }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Limite mensuelle (jours)"
                type="number"
                value={localLimitDays}
                onChange={handleLimitDaysChange}
                onBlur={handleLimitDaysBlur}
                inputProps={{ 
                  step: 1,
                  min: 0
                }}
                fullWidth
              />
              <TextField
                label="Limite mensuelle (heures)"
                type="number"
                value={localLimitHours}
                onChange={handleLimitHoursChange}
                onBlur={handleLimitHoursBlur}
                inputProps={{ 
                  step: 0.25,
                  min: 0
                }}
                fullWidth
              />
            </Box>
            <TextField
              name="destination"
              label="Destination"
              value={currentCode.destination}
              onChange={handleChange}
              required />
            <FormControl>
              <InputLabel>{validiteLabel}</InputLabel>
              <Select
                name="particularity"
                value={currentCode.particularity}
                onChange={handleChange}
                label={validiteLabel}
              >
                {Object.entries(PARTICULARITY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="role"
              label={roleLabel}
              value={currentCode.role}
              onChange={handleChange} />
            <TextField
              name="mandate_description"
              label="Description"
              value={currentCode.mandate_description}
              onChange={handleChange}
              multiline
              rows={3} />
            <FormControl fullWidth>
              <ColorPicker
                value={currentCode.color}
                onChange={(color) => handleChange({ target: { name: 'color', value: color }})}
                label="Couleur"
              />
            </FormControl>
            <FormControl>
              <InputLabel>Statut</InputLabel>
              <Switch
                name="is_active"
                checked={currentCode.is_active}
                onChange={handleChange} />
            </FormControl>
            <FormControl>
              <InputLabel>Inclure dans le taux de relève</InputLabel>
              <Switch
                name="include_in_releve_rate"
                checked={currentCode.include_in_releve_rate}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <InputLabel>Sans Mandat</InputLabel>
              <Switch
                name="without_mandate"
                checked={currentCode.without_mandate}
                onChange={handleChange}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
}

export default ReleveCodeManagement;
